import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Clock, Play, Square, Trash2, History, Timer, MapPin, Pencil } from "lucide-react";

const manage = (r) => ["admin", "manager"].includes(r);

export const TicketTimeTracking = ({ ticketId, ticket, onChange }) => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const locale = lang === "en" ? "en-US" : "pt-BR";
  const [entries, setEntries] = useState([]);
  const [activity, setActivity] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editVals, setEditVals] = useState({ clock_in: "", clock_out: "" });

  const refresh = async () => {
    const fresh = await api.get(`/tickets/${ticketId}`);
    setEntries(fresh.time_entries || []);
    setActivity((fresh.activity || []).slice().reverse());
    onChange && onChange();
  };
  useEffect(() => { refresh(); }, [ticketId]); // eslint-disable-line

  const myOpen = entries.find((e) => e.tech_id === user.id && !e.clock_out);
  const anyOpen = entries.find((e) => !e.clock_out);

  const getPosition = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  });

  const clockIn = async () => {
    setBusy(true);
    try { const loc = await getPosition(); await api.post(`/tickets/${ticketId}/clock-in`, loc || {}); toast.success(t("clockIn")); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };
  const clockOut = async () => {
    setBusy(true);
    try { const loc = await getPosition(); await api.post(`/tickets/${ticketId}/clock-out`, loc || {}); toast.success(t("clockOut")); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };
  const removeEntry = async (id) => {
    await api.del(`/tickets/${ticketId}/time-entry/${id}`);
    refresh();
  };
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso); const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };
  const saveEdit = async (id) => {
    try {
      await api.put(`/tickets/${ticketId}/time-entry/${id}`, {
        clock_in: editVals.clock_in ? new Date(editVals.clock_in).toISOString() : undefined,
        clock_out: editVals.clock_out ? new Date(editVals.clock_out).toISOString() : undefined,
      });
      setEditId(null); refresh();
    } catch { toast.error(t("error")); }
  };

  const totalHours = entries.reduce((s, e) => s + (e.hours || 0), 0);
  const totalCost = entries.reduce((s, e) => s + (e.cost || 0), 0);
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(locale, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const actLabel = (a) => ({
    scheduled: t("actScheduled"), assigned: `${t("actAssigned")} ${a.detail}`,
    status: `${t("actStatus")} ${t(a.detail) || a.detail}`,
    clock_in: t("actClockIn"), clock_out: `${t("actClockOut")} (${a.detail})`,
  }[a.action] || a.action);

  const canClock = user.role === "technician" ? ticket?.assigned_to === user.id : manage(user.role);

  return (
    <div className="border-t border-slate-200 pt-4 space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Timer className="w-4 h-4 text-slate-500" />
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("timeTracking")}</span>
          </div>
          {canClock && (
            myOpen ? (
              <button data-testid="clock-out-btn" disabled={busy} onClick={clockOut} className="inline-flex items-center gap-1.5 text-xs font-medium bg-red-600 text-white rounded-lg px-3 py-1.5 hover:bg-red-700 disabled:opacity-60">
                <Square className="w-3.5 h-3.5" /> {t("clockOut")}
              </button>
            ) : (
              <button data-testid="clock-in-btn" disabled={busy || (anyOpen && user.role === "technician")} onClick={clockIn} className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-60">
                <Play className="w-3.5 h-3.5" /> {t("clockIn")}
              </button>
            )
          )}
        </div>

        {entries.length > 0 ? (
          <div className="space-y-1.5">
            {entries.map((e) => (
              <div key={e.id} data-testid={`time-entry-${e.id}`} className="bg-slate-50 rounded-lg px-3 py-2">
                {editId === e.id ? (
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <input data-testid={`edit-in-${e.id}`} type="datetime-local" value={editVals.clock_in} onChange={(ev) => setEditVals({ ...editVals, clock_in: ev.target.value })} className="rounded border border-slate-300 px-2 py-1" />
                    <span>→</span>
                    <input data-testid={`edit-out-${e.id}`} type="datetime-local" value={editVals.clock_out} onChange={(ev) => setEditVals({ ...editVals, clock_out: ev.target.value })} className="rounded border border-slate-300 px-2 py-1" />
                    <button data-testid={`edit-save-${e.id}`} onClick={() => saveEdit(e.id)} className="bg-emerald-600 text-white rounded px-2 py-1 font-medium">{t("save")}</button>
                    <button onClick={() => setEditId(null)} className="border border-slate-300 rounded px-2 py-1">{t("cancel")}</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="text-slate-600 truncate flex-1">{e.tech_name} · {fmt(e.clock_in)} → {e.clock_out ? fmt(e.clock_out) : <span className="text-emerald-600 font-medium">{t("running")}</span>}</span>
                    {(e.clock_in_loc || e.clock_out_loc) && (
                      <a data-testid={`time-loc-${e.id}`} href={`https://maps.google.com/?q=${(e.clock_out_loc || e.clock_in_loc).lat},${(e.clock_out_loc || e.clock_in_loc).lng}`} target="_blank" rel="noreferrer" title={t("viewOnMap")} className="text-blue-500 hover:text-blue-700"><MapPin className="w-3.5 h-3.5" /></a>
                    )}
                    <span className="tabular-nums text-slate-700 font-medium">{(e.hours || 0).toFixed(2)}h</span>
                    <span className="tabular-nums text-slate-500 w-16 text-right">{money(e.cost)}</span>
                    {manage(user.role) && (
                      <>
                        <button data-testid={`edit-entry-${e.id}`} onClick={() => { setEditId(e.id); setEditVals({ clock_in: toLocalInput(e.clock_in), clock_out: toLocalInput(e.clock_out) }); }} className="text-slate-400 hover:text-slate-700" title={t("editEntry")}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => removeEntry(e.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-slate-200">
              <span className="text-slate-600">{t("hours")}: {totalHours.toFixed(2)}h</span>
              <span className="text-slate-900">{t("laborCost")}: {money(totalCost)}</span>
            </div>
          </div>
        ) : <p className="text-xs text-slate-400 py-1">{t("noActivity")}</p>}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <History className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("activity")}</span>
        </div>
        {activity.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {activity.map((a) => (
              <div key={a.id} className="flex items-start gap-2 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                <div>
                  <span className="text-slate-700"><span className="font-medium">{a.by}</span> {actLabel(a)}</span>
                  <div className="text-slate-400">{fmt(a.at)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-slate-400">{t("noActivity")}</p>}
      </div>
    </div>
  );
};
